import React, {Component} from 'react';
import {Form, Input, Row, Col, InputNumber, Button, notification, Checkbox, Tooltip} from 'antd';
import {CheckboxChangeEvent} from 'antd/lib/checkbox';
import {Link} from 'react-router-dom';

import {Region, Club, Starterpack, StarterpackText, Language} from '../../../../interfaces';
import {UpdateStarterpackInput} from '../QueryMutation';

require('react-quill/dist/quill.snow.css');
const ReactQuill = require('react-quill');

const FormItem = Form.Item;

interface Properties {
  club: Club;
  region: Region;
  starterpack: Starterpack;
  price: any;
  languages: Array<Language>;
  updateStarterpack: (data: UpdateStarterpackInput) => Promise<Starterpack>;
  isDefaultSelected: boolean;
}

interface State {
  priority: number;
  active: boolean;
  ospAvailable: boolean;
  dspAvailable: boolean;
  isDefault: boolean;
  texts: Array<StarterpackText>;
}

class StarterpackDetail extends Component<Properties, State> {
  constructor(props: Properties) {
    super(props);

    this.state = {
      priority: props.starterpack.priority,
      active: props.starterpack.active,
      ospAvailable: props.starterpack.ospAvailable,
      dspAvailable: props.starterpack.dspAvailable,
      isDefault: props.starterpack.isDefault || false,
      texts: props.languages
        .map((lang: Language) => {
          const relatedText = props.starterpack.texts.find(text => text.language.id === lang.id);
          return {
            id: relatedText ? relatedText.id : null,
            title: relatedText ? relatedText.title : '',
            description: relatedText ? relatedText.description : '',
            language: relatedText ? relatedText.language : lang
          };
        })
        .sort((a: StarterpackText, b: StarterpackText): number => parseInt(b.id, 10) - parseInt(a.id, 10))
    };
  }

  UNSAFE_componentWillReceiveProps(props: Properties): void {
    this.setState({
      priority: props.starterpack.priority,
      active: props.starterpack.active,
      ospAvailable: props.starterpack.ospAvailable,
      dspAvailable: props.starterpack.dspAvailable,
      isDefault: props.starterpack.isDefault,
      texts: props.languages
        .map((lang: Language) => {
          const relatedText = props.starterpack.texts.find(text => text.language.id === lang.id);
          return {
            id: relatedText ? relatedText.id : null,
            title: relatedText ? relatedText.title : '',
            description: relatedText ? relatedText.description : '',
            language: relatedText ? relatedText.language : lang
          };
        })
        .sort((a: StarterpackText, b: StarterpackText): number => parseInt(b.id, 10) - parseInt(a.id, 10))
    });
  }

  updatePriority = (value: React.ReactText): void => {
    const priority = parseInt(value.toString(), 10);
    this.setState({priority});
  };

  updateText = (lang: string, type: string, value: string) => {
    const oldTexts = this.state.texts;
    const texts = [];
    for (let text of oldTexts) {
      if (text.language.code === lang) {
        text[type] = value;
        texts.push(text);
        continue;
      }
      texts.push(text);
    }
    this.setState({texts});
  };

  toggleActive = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({active: checked});
  };

  toggleOsp = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({ospAvailable: checked});
  };

  toggleDsp = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({dspAvailable: checked});
  };
  toggleDefault = ({target: {checked}}: CheckboxChangeEvent) => {
    this.setState({isDefault: checked});
  };

  getFormTexts(): Array<JSX.Element> {
    return this.state.texts.map((text, index) => {
      return (
        <div key={text.id || index} style={{marginBottom: 24}}>
          <div
            style={{
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid #999',
              fontWeight: 600,
              textTransform: 'capitalize'
            }}
          >
            {text.language.name}
          </div>
          <FormItem label="Title" validateStatus={!text.title.length ? 'error' : null}>
            <Input type="text" value={text.title} onChange={({target: {value}}) => this.updateText(text.language.code, 'title', value)} />
          </FormItem>
          <FormItem label="Description">
            <ReactQuill
              value={text.description}
              onChange={(value: string) => this.updateText(text.language.code, 'description', value)}
              modules={{
                toolbar: [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}], ['clean']]
              }}
            />
          </FormItem>
        </div>
      );
    });
  }

  validate(): boolean {
    let valid: boolean = true;

    if (!Number.isInteger(this.state.priority)) {
      valid = false;
    }

    for (let text of this.state.texts) {
      if (!text.title) {
        valid = false;
        break;
      }
    }

    return valid;
  }

  handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!this.validate()) {
      notification.error({
        message: 'Form is not valid',
        description: 'Please fill in all required fields.'
      });
      return;
    }

    this.props
      .updateStarterpack({
        variables: {
          data: {
            id: Number(this.props.starterpack.id) || null,
            externalStarterPackId: this.props.starterpack.externalStarterPackId,
            active: this.state.active || false,
            priority: this.state.priority || 0,
            ospAvailable: this.state.ospAvailable || false,
            dspAvailable: this.state.dspAvailable || false,
            isDefault: this.state.isDefault || false,
            texts: this.state.texts.map(text => ({
              id: text.id || null,
              languageId: text.language.id,
              title: text.title || '',
              description: text.description || ''
            }))
          }
        }
      })
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Starterpack has been updated!'
        });
      });
  };

  render(): JSX.Element {
    const {starterpack, club, region, price, isDefaultSelected} = this.props;
    return (
      <Form onSubmit={this.handleSubmit}>
        <Row style={{padding: 16, background: '#fff'}}>
          <Col span={12} style={{padding: 16}}>
            <FormItem label="Region">
              <Input type="text" disabled value={region.name} />
            </FormItem>
            <FormItem label="Club">
              <Input type="text" disabled value={club.name} />
            </FormItem>
            <FormItem label="Starterpack code">
              <Input type="text" disabled value={starterpack.externalStarterPackId} />
            </FormItem>
            <FormItem label="Price">
              <Input type="text" disabled value={price && price.price} />
            </FormItem>
            <FormItem label="Priority" validateStatus={!Number.isInteger(this.state.priority) ? 'error' : null}>
              <InputNumber value={this.state.priority} onChange={this.updatePriority} />
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.active} onChange={this.toggleActive}>
                {'Active'}
              </Checkbox>
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.ospAvailable} onChange={this.toggleOsp}>
                {'Available in online sales process'}
              </Checkbox>
            </FormItem>
            <FormItem>
              <Checkbox checked={this.state.dspAvailable} onChange={this.toggleDsp}>
                {'Available in digital sales process'}
              </Checkbox>
            </FormItem>
            <FormItem>
              <Tooltip title={isDefaultSelected ? 'default starterpack already exist' : ''}>
                <Checkbox checked={this.state.isDefault} disabled={isDefaultSelected} onChange={this.toggleDefault}>
                  {'Make it Default'}
                </Checkbox>
              </Tooltip>
            </FormItem>
          </Col>
          <Col span={12} style={{padding: 16}}>
            {this.getFormTexts()}
          </Col>
          <Col span={24} style={{textAlign: 'right'}}>
            <Button type="default" style={{marginRight: 8}}>
              <Link to={`/starterpacks/${this.props.region.id}/${this.props.club.id}`}>Back</Link>
            </Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    );
  }
}

export default StarterpackDetail;



// WEBPACK FOOTER //
// ./src/pages/StarterpacksPage/components/StarterpackDetail/components/StarterpackDetail/StarterpackDetail.tsx